// src/components/demo/DemoPhotoCapture.js
//
// Optional rider photo step (SPEC §4.1, round-3). Shown only when the spot's
// photoCapture setting is 'face' or 'id'. Uses the OS camera via
// <input capture> — reliable across mobile browsers, no getUserMedia edge
// cases while the scanner may hold the camera. Downscales to ≤900px JPEG and
// returns a data URL (moved to Storage by the Cloud Function).
//
// Never blocks a check-out: SKIP records photoDeclined.

import React, { useRef, useState } from 'react';
import { FBtn, FLabel } from '../foam/FoamPrimitives';

const MAX_DIM = 900;
const JPEG_Q = 0.7;

async function fileToDataUrl(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', JPEG_Q);
}

export default function DemoPhotoCapture({ mode /* 'face' | 'id' */, onDone }) {
  const inputRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const label = mode === 'id' ? 'Photo of rider’s ID' : 'Photo of rider';

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow retake of the same file
    if (!file) return;
    setBusy(true);
    try {
      setDataUrl(await fileToDataUrl(file));
    } catch {
      setDataUrl(null);
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-[14px]">
      <FLabel>{label}</FLabel>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        style={{ display: 'none' }}
      />
      {dataUrl ? (
        <>
          <img
            src={dataUrl}
            alt=""
            className="w-full object-cover"
            style={{ aspectRatio: '4 / 3', border: '1.5px solid var(--foam-ink)' }}
          />
          <FBtn primary onClick={() => onDone({ photoDataUrl: dataUrl, photoType: mode, photoDeclined: false })}>
            Use photo
          </FBtn>
          <FBtn ghost onClick={() => inputRef.current.click()} disabled={busy}>
            Retake
          </FBtn>
        </>
      ) : (
        <>
          <FBtn primary onClick={() => inputRef.current.click()} disabled={busy}>
            {busy ? 'Processing…' : 'Take photo'}
          </FBtn>
          <FBtn ghost onClick={() => onDone({ photoDataUrl: null, photoType: null, photoDeclined: true })}>
            Skip — rider declined
          </FBtn>
        </>
      )}
    </div>
  );
}
